import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { getDefaultCountryCode } from './phoneNumberUtils';
import { capitalizeWords } from './stringUtils';

/**
 * Map stored prelead countryCode (+91 / 91 / IN) to ISO used by AddLeadForm.
 */
export function countryCodeToIso(countryCode) {
  if (!countryCode || typeof countryCode !== 'string') return getDefaultCountryCode();
  const raw = countryCode.trim();
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();

  const digits = raw.replace(/^\+/, '').replace(/\D/g, '');
  if (!digits) return getDefaultCountryCode();

  try {
    const match = getCountries().find((iso) => String(getCountryCallingCode(iso)) === digits);
    return match || getDefaultCountryCode();
  } catch {
    return getDefaultCountryCode();
  }
}

/**
 * Resolve project for qualifying a prelead (inbound ads vs interested WA campaign).
 */
export function resolvePreleadQualifyProjectId(row, { isInboundAdsList = false, fallbackProjectId = null } = {}) {
  if (isInboundAdsList) {
    const n = Number(row?.project_id ?? fallbackProjectId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  const interestedCampaignSend = row?.whatsapp_campaign_sends
    ?.filter((send) => String(send?.state ?? '').trim().toLowerCase() === 'interested')
    .sort((a, b) => new Date(b?.sent_at ?? 0) - new Date(a?.sent_at ?? 0))[0];
  const fromCampaign = Number(interestedCampaignSend?.WhatsAppCampaign?.project_id);
  if (Number.isFinite(fromCampaign) && fromCampaign > 0) return fromCampaign;

  const fallback = Number(fallbackProjectId ?? row?.project_id);
  return Number.isFinite(fallback) && fallback > 0 ? fallback : null;
}

/**
 * Prefill values for AddLeadForm when converting a prelead.
 */
export function buildAddLeadInitialValuesFromPrelead(row, { projectId, source } = {}) {
  if (!row) return null;

  const countryIso = countryCodeToIso(row.countryCode);
  const email = row.email && row.email !== '—' ? row.email : '';
  const alternate =
    row.alternate_phone_number && row.alternate_phone_number !== '—'
      ? row.alternate_phone_number
      : '';

  let expectedBudgetRange = '';
  if (row.min_estimated_budget != null && row.max_estimated_budget != null) {
    expectedBudgetRange = `${row.min_estimated_budget}-${row.max_estimated_budget}`;
  } else if (row.expected_budget != null && Number(row.expected_budget) > 0) {
    expectedBudgetRange = String(row.expected_budget);
  }

  return {
    name: capitalizeWords(String(row.name || '').trim()) || '',
    countryCode: countryIso,
    phone_number: String(row.phone || '').replace(/\D/g, ''),
    alternate_phone_number: String(alternate).replace(/\D/g, ''),
    email,
    occupation: row.occupation || '',
    reference_name: row.reference_name || '',
    address: row.address || '',
    gender: row.gender || '',
    source: source || row.source || '',
    interested_projects: projectId != null ? String(projectId) : '',
    expected_clouser_date: row.expected_closure_date || '',
    expectedBudgetRange,
    accomodations: Array.isArray(row.accommodations) ? row.accommodations : [],
    plotArea: Array.isArray(row.plotArea) ? row.plotArea : [],
    project_accommodations: Array.isArray(row.project_accommodations)
      ? row.project_accommodations
      : []
  };
}
